import React, { useState, useEffect } from 'react';
import { Modal, List, Button, Input, Space, Typography, Popconfirm, Form } from 'antd';
import { DeleteOutlined, EditOutlined, PlusOutlined } from '@ant-design/icons';
import { FormSection } from '../../../common/typescript/models/CollectFormV2';

interface SectionManagerModalProps {
    visible: boolean;
    sections: FormSection[];
    onClose: () => void;
    onAdd: (section: FormSection) => void;
    onUpdate: (id: string, section: FormSection) => void;
    onDelete: (id: string) => void;
}

export const SectionManagerModal: React.FC<SectionManagerModalProps> = ({
    visible,
    sections,
    onClose,
    onAdd,
    onUpdate,
    onDelete,
}) => {
    const [isEditing, setIsEditing] = useState(false);
    const [currentSection, setCurrentSection] = useState<Partial<FormSection>>({});
    const [form] = Form.useForm();

    const handleEdit = (section: FormSection) => {
        setCurrentSection(section);
        form.setFieldsValue(section);
        setIsEditing(true);
    };

    const handleAddNew = () => {
        setCurrentSection({});
        form.resetFields();
        setIsEditing(true);
    };

    const handleSave = () => {
        form.validateFields().then((values) => {
            const newSection: FormSection = {
                id: values.id,
                title: values.title,
                description: values.description,
                fields: currentSection.fields || [],
                collapsible: true, // Default
            };

            if (currentSection.id) {
                onUpdate(currentSection.id, newSection);
            } else {
                onAdd(newSection);
            }
            setIsEditing(false);
            form.resetFields();
        });
    };

    return (
        <Modal
            title="Quản lý các phần (Sections)"
            open={visible}
            onCancel={onClose}
            footer={[
                <Button key="close" onClick={onClose}>
                    Đóng
                </Button>,
            ]}
        >
            {!isEditing ? (
                <>
                    <Button type="primary" icon={<PlusOutlined />} onClick={handleAddNew} block style={{ marginBottom: 16 }}>
                        Thêm phần mới
                    </Button>
                    <List
                        dataSource={sections}
                        renderItem={(item) => (
                            <List.Item
                                actions={[
                                    <Button key="edit" type="text" icon={<EditOutlined />} onClick={() => handleEdit(item)} />,
                                    <Popconfirm title="Xóa phần này?" onConfirm={() => onDelete(item.id)}>
                                        <Button key="delete" type="text" danger icon={<DeleteOutlined />} />
                                    </Popconfirm>,
                                ]}
                            >
                                <List.Item.Meta
                                    title={item.title}
                                    description={item.id}
                                />
                            </List.Item>
                        )}
                    />
                </>
            ) : (
                <Form form={form} layout="vertical">
                    <Form.Item name="id" label="ID Phần" rules={[{ required: true }, { pattern: /^[a-z_][a-z0-9_]*$/, message: 'ID không hợp lệ' }]}>
                        <Input disabled={!!currentSection.id} placeholder="vd: basic_info" />
                    </Form.Item>
                    <Form.Item name="title" label="Tiêu đề" rules={[{ required: true }]}>
                        <Input placeholder="vd: Thông tin cơ bản" />
                    </Form.Item>
                    <Form.Item name="description" label="Mô tả">
                        <Input.TextArea />
                    </Form.Item>
                    <Space>
                        <Button onClick={() => setIsEditing(false)}>Hủy</Button>
                        <Button type="primary" onClick={handleSave}>Lưu</Button>
                    </Space>
                </Form>
            )}
        </Modal>
    );
};
